import { useState, useEffect } from "react";
import axios from "axios";
import { X, Upload, Save } from "lucide-react";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isAllowedImage = (file) => {
  const extension = `.${(file?.name || "").split(".").pop()?.toLowerCase()}`;
  return ALLOWED_IMAGE_TYPES.includes(file?.type) && ALLOWED_IMAGE_EXTENSIONS.includes(extension);
};

const Editsubmission = ({ isOpen, onClose, submission, onUpdate }) => {
  const [formData, setFormData] = useState({ title: "", description: "", member_bio: "", image: null });
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    if (submission) {
      setFormData({ title: submission.title || "", description: submission.description || "", member_bio: submission.member_bio || "", image: null });
      setPreview(submission.image || null);
      setError("");
    }
  }, [submission]);

  if (!isOpen) return null;

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!isAllowedImage(file)) {
      setFormData((p) => ({ ...p, image: null }));
      setError("Only JPG, PNG, or WEBP image files can be uploaded.");
      e.target.value = "";
      return;
    }

    setError("");
    setFormData((p) => ({ ...p, image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { setError("Title is required."); return; }
    if (formData.image && !isAllowedImage(formData.image)) {
      setError("Only JPG, PNG, or WEBP image files can be uploaded.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = new FormData();
      payload.append("data", JSON.stringify({ title: formData.title, description: formData.description, member_bio: formData.member_bio }));
      if (formData.image) payload.append("pfp", formData.image);

      const res = await axios.put(`/api/quest/edit/${submission._id}`, payload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        onUpdate?.();
        onClose();
      } else {
        setError(res.data.message || "Update failed. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Update failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-cinzel text-lg font-bold text-gray-900">Edit Submission</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 font-outfit text-sm">
              {error}
            </div>
          )}

          {/* Image */}
          <div>
            <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Submission Image
            </label>
            <label htmlFor="editImage"
              className="cursor-pointer rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-6 flex flex-col items-center justify-center hover:bg-gray-100 transition-colors">
              <input type="file" id="editImage" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />
              {preview ? (
                <img src={preview} alt="preview" className="h-32 w-auto object-contain rounded-xl mb-2" />
              ) : (
                <Upload size={24} className="text-gray-300 mb-2" strokeWidth={1.5} />
              )}
              <p className="font-outfit text-xs text-gray-400">{preview ? "Click to change" : "Click to upload"}</p>
            </label>
            <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-outfit text-xs text-gray-400">
                Accepted formats: JPG, PNG, WEBP.
              </p>
              {formData.image && (
                <p className="font-outfit text-xs font-medium text-gray-500">
                  Selected: {formData.image.name} ({formatFileSize(formData.image.size)})
                </p>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input name="title" value={formData.title} onChange={handleChange}
              className="w-full rounded-full border border-gray-200 px-4 py-2.5 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition"
              placeholder="Title of your work" />
          </div>

          {/* Description */}
          <div>
            <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Description
            </label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={4}
              className="w-full rounded-2xl border border-gray-200 px-4 py-2.5 text-sm font-outfit text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-cyan-300 transition"
              placeholder="Describe your work..." />
          </div>

          {/* Member Bio */}
          <div>
            <label className="block font-outfit text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              Member Bio
            </label>
            <input name="member_bio" value={formData.member_bio} onChange={handleChange}
              className="w-full rounded-full border border-gray-200 px-4 py-2.5 text-sm font-outfit text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 transition"
              placeholder="Short bio (optional)" />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-full border border-gray-300 text-gray-600 font-outfit text-sm font-medium hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-outfit text-sm font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <><Save size={14} strokeWidth={2.5} /> Save Changes</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Editsubmission;
